import { useForm } from 'react-hook-form'
import { useMemo, useCallback, useEffect, useState } from 'react'
import { useEnterpriseAccountLogIn } from 'domains/public/api/auth/log-in/enterpriseAccountLogIn.hooks'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import { emailValidationPattern } from 'utils/validators/emailValidation'
import { type DashMessages } from 'intl/intl.types'
import {
  isInvalidCredentialsError,
  isDisabledAccountError,
  isInvalidCaptchaError,
} from 'api/utils/apollo/errors/isError.hooks'
import clientEnv from 'utils/global/clientEnv'
import { useEnterpriseAccountPreflight } from 'domains/public/api/auth/preflight/enterpriseAccountPreflight.hooks'
import { redirectToAuthService } from 'domains/public/components/log-in/utils/redirectToAuthService'

type AuthLogInFormEmailData = {
  email: string
}

type AuthLogInFormPasswordData = {
  password: string
  reCaptchaToken: string
}

const defaultEmailValues: AuthLogInFormEmailData = {
  email: '',
}

const defaultPasswordValues: AuthLogInFormPasswordData = {
  password: '',
  reCaptchaToken: '',
}

export const usePublicAuthLogInForm = () => {
  const [disableEmailForm, setDisableEmailForm] = useState(false)

  const {
    logInAccount,
    apiSuccess: logInSuccess,
    apiError: logInError,
    apiLoading: logInLoading,
  } = useEnterpriseAccountLogIn()

  const {
    preflightCheck,
    apiResult: preflightResult,
    apiError: preflightError,
    apiLoading: preflightLoading,
  } = useEnterpriseAccountPreflight()

  // We hide password/captcha field by default until we capture user email
  // Once we have email, we decide where to send them for authentication
  const [showPasswordAndCaptcha, setShowPasswordAndCaptcha] = useState(false)
  const [email, setEmail] = useState('')

  const resetToEmail = function () {
    setDisableEmailForm(false)
    setShowPasswordAndCaptcha(false)
  }

  const {
    register: emailRegister,
    handleSubmit: handleEmailSubmit,
    errors: emailErrors,
    setValue: emailSetValue,
  } = useForm<AuthLogInFormEmailData>({
    defaultValues: defaultEmailValues,
  })

  const {
    register: passwordRegister,
    handleSubmit: handlePasswordSubmit,
    errors: passwordErrors,
    setValue: passwordSetValue,
  } = useForm<AuthLogInFormPasswordData>({
    defaultValues: defaultPasswordValues,
  })

  const onPasswordSubmit = handlePasswordSubmit(({ password, reCaptchaToken }) => {
    logInAccount({
      email,
      password,
      reCaptchaToken,
    })
  })

  const onEmailSubmit = handleEmailSubmit(({ email }) => {
    const trimmedEmail = email.trim()
    setDisableEmailForm(true)
    setEmail(trimmedEmail)
    preflightCheck({ email: trimmedEmail })
  })

  useEffect(() => {
    if (email && !preflightLoading && preflightResult) {
      if (preflightResult.useAuthService) {
        redirectToAuthService(email)
      } else {
        setShowPasswordAndCaptcha(true)
      }
    }

    if (email && preflightError) {
      setDisableEmailForm(false)
    }
  }, [
    email,
    preflightLoading,
    preflightResult,
    preflightError,
    setShowPasswordAndCaptcha,
    setDisableEmailForm,
  ])

  let requireCaptcha = true
  // Captcha is still required by API
  if (
    clientEnv.PUBLIC_CLIENT_CYPRESS_TOKEN &&
    clientEnv.PUBLIC_CLIENT_ENVIRONMENT !== 'production'
  ) {
    requireCaptcha = false
  }

  const emailFieldRef = emailRegister({ required: true, pattern: emailValidationPattern })
  const passwordFieldRef = passwordRegister({ required: true, minLength: 8 })
  const registerReCaptchaTokenField = useCallback(
    () => passwordRegister({ name: 'reCaptchaToken' }, { required: requireCaptcha }),
    [passwordRegister, requireCaptcha]
  )
  const setReCaptchaTokenValue = useCallback(
    (value: string, shouldValidate = false) => {
      passwordSetValue('reCaptchaToken', value, shouldValidate)
    },
    [passwordSetValue]
  )

  // If we get an error from the API, we want to reset our captcha
  const [reCaptchaResetHandler, setReCaptchaResetHandler] = useState<() => void>(null)

  useEffect(() => {
    if (logInError && reCaptchaResetHandler) {
      setReCaptchaTokenValue('', false)
      reCaptchaResetHandler()
    }
  }, [setReCaptchaTokenValue, reCaptchaResetHandler, logInError])

  const emailErrorMessageId = useFormErrorMessageMap({
    required: 'public.logIn.form.fields.email.error.required',
    pattern: 'public.logIn.form.fields.email.error.pattern',
  })(emailErrors.email)

  const passwordErrorMessageId = useFormErrorMessageMap({
    required: 'public.logIn.form.fields.password.error.required',
    minLength: 'public.logIn.form.fields.password.error.minLength',
  })(passwordErrors.password)

  const reCaptchaTokenErrorMessageId = useFormErrorMessageMap({
    required: 'public.logIn.form.fields.reCaptchaToken.error.required',
  })(passwordErrors.reCaptchaToken)

  const formErrorMessageId: DashMessages | undefined = useMemo(() => {
    if (preflightError) {
      return 'public.logIn.form.error.common'
    }

    if (!logInError) return undefined

    if (isInvalidCredentialsError(logInError)) {
      return 'public.logIn.form.error.invalidCredentials'
    }

    if (isInvalidCaptchaError(logInError)) {
      return 'public.logIn.form.error.captchaFailed'
    }

    if (isDisabledAccountError(logInError)) {
      return 'public.logIn.form.error.disabledAccount'
    }

    return 'public.logIn.form.error.common'
  }, [logInError, preflightError])

  return {
    logInSuccess,
    disableFormFields: logInLoading || logInSuccess,
    onPasswordSubmit,
    onEmailSubmit,
    emailFieldRef,
    showPasswordAndCaptcha,
    resetToEmail,
    passwordFieldRef,
    setReCaptchaResetHandler,
    registerReCaptchaTokenField,
    setReCaptchaTokenValue,
    emailErrorMessageId,
    passwordErrorMessageId,
    reCaptchaTokenErrorMessageId,
    formErrorMessageId,
    disableEmailForm,
    setEmail,
    email,
    _emailSetValue: emailSetValue,
    _passwordSetValue: passwordSetValue,
  }
}
